import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DealerNav from "./DealerNav";
import { getVehiclePath } from "../Utils/vehicleUrls";

const API = process.env.REACT_APP_API_BASE;

export default function DealerDashboard() {
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem("dealerToken"), []);
  const [data, setData] = useState(null);
  const [ts, setTs] = useState(null);                   // time-series
  const [top, setTop] = useState({ by: "carViews", rows: [] }); // top listings
  const [slas, setSlas] = useState(null);               // <— NEW
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const today = new Date();
  const d30 = new Date(today); d30.setDate(today.getDate() - 30);
  const [start, setStart] = useState(fmtYmd(d30));
  const [end, setEnd] = useState(fmtYmd(today));

  const authFetch = async (url) => {
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (r.status === 401) { navigate("/dealer/login", { replace: true }); return null; }
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  };

  const load = async () => {
    if (!token) return navigate("/dealer/login", { replace: true });
    setLoading(true); setErr("");
    const qs = new URLSearchParams();
    if (start) qs.set("start", start);
    if (end) qs.set("end", end);
    try {
      const [summary, series, topRows, slaInfo] = await Promise.all([
        authFetch(`${API}/dealer/metrics?${qs}`),
        authFetch(`${API}/dealer/metrics/timeseries?${qs}`),
        authFetch(`${API}/dealer/metrics/top?${qs}&by=${top.by}&limit=5`),
        authFetch(`${API}/dealer/metrics/slas?hours=24`) // <— NEW
      ]);
      if (!summary) return; // redirected
      setData(summary || null);
      setTs(series || null);
      setTop((t) => ({ ...t, rows: topRows || [] }));
      setSlas(slaInfo || null); // <— NEW
    } catch {
      setErr("Could not load metrics.");
      setData(null); setTs(null); setTop((t) => ({ ...t, rows: [] }));
      setSlas(null); // <— NEW
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onApply = () => load();

  const changeTopBy = async (by) => {
    setTop((t) => ({ ...t, by }));
    const qs = new URLSearchParams();
    if (start) qs.set("start", start);
    if (end) qs.set("end", end);
    try {
      const rows = await authFetch(`${API}/dealer/metrics/top?${qs}&by=${by}&limit=5`);
      if (rows) setTop({ by, rows });
    } catch {}
  };

  // summary numbers
  const leadsTotal = data?.leads?.total ?? 0;
  const unread = data?.leads?.unread ?? 0;
  const byStatus = mapStatus(data?.leads?.byStatus);
  const avgSec = data?.response?.firstReplySecondsAvg ?? null;
  const medSec = data?.response?.firstReplySecondsMedian ?? null;
  const activeInv = data?.inventory?.active ?? 0;
  const soldInWindow = data?.inventory?.soldInWindow ?? 0;
  const avgDom = data?.inventory?.avgDaysOnMarket ?? null;

  return (
    <div className="max-w-6xl mx-auto p-4">
      <DealerNav />

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Dealer Dashboard</h1>
          <p className="text-sm text-gray-600">Performance for {start} → {end}</p>
        </div>
        <div className="flex gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Start</label>
            <input type="date" value={start} onChange={(e)=>setStart(e.target.value)}
                   className="h-10 px-3 rounded-lg border border-gray-300" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">End</label>
            <input type="date" value={end} onChange={(e)=>setEnd(e.target.value)}
                   className="h-10 px-3 rounded-lg border border-gray-300" />
          </div>
          <button onClick={onApply} className="self-end h-10 px-4 rounded-lg bg-blue-600 text-white">Apply</button>
        </div>
      </div>

      {err && <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      {loading && <div className="mb-4 text-gray-500">Loading…</div>}

      {!loading && data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
            <Card title="Total Leads" value={leadsTotal} foot={`${unread} unread`} />
            <Card title="Response (avg)" value={fmtDur(avgSec) ?? "—"} foot={`Median ${fmtDur(medSec) ?? "—"}`} />
            <Card title="Active Inventory" value={activeInv} foot={`${soldInWindow} sold in window`} />
          <Card
  title={
    <button
      onClick={() =>
         navigate(`/dealer/leads?filter=overdue${slas?.hours ?? 24}`)
      }
      className="underline"
    >
      Overdue Leads ({slas?.hours ?? 24}h)
    </button>
  }
  value={slas?.leadsOverdue ?? 0}
  foot={`${slas?.leadsOverdueUnread ?? 0} unread`}
/>



          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
            <Card title="Car Views" value={data?.engagement?.carViews ?? 0} foot="Detail page views" />
            <Card title="Comps Opens" value={data?.engagement?.compsOpens ?? 0} foot="Sold comps viewed" />
            <Card title="Contact @ Mestimate" value={data?.engagement?.contactAtMestimate ?? 0} foot="Buyers reached out" />
          </div>

          {/* Status + Inventory panels */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Leads by Status">
              <ul className="space-y-2 text-sm">
                {["New","Contacted","Qualified","Closed"].map(s=>(
                  <li key={s} className="flex justify-between">
                    <span className="text-gray-700">{s}</span>
                    <span className="font-semibold">{byStatus[s] ?? 0}</span>
                  </li>
                ))}
              </ul>
            </Panel>

            <Panel title="Inventory Health">
              <div className="text-sm text-gray-700">
                <span className="text-gray-600">Avg Days on Market:</span>{" "}
                <span className="font-semibold">{avgDom!=null?avgDom.toFixed(1):"—"}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">Based on vehicles sold in range.</div>
            </Panel>
          </div>

          {/* Trends + Top */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <Panel title="Engagement Trends (daily)">
              <div className="text-xs text-gray-500 mb-2">Last {ts?.dates?.length ?? 0} days</div>
              {ts ? (
                <>
                  <TinyLine label="Leads" data={ts.leads} />
                  <TinyLine label="Car Views" data={ts.carViews} />
                  <TinyLine label="Comps Opens" data={ts.compsOpens} />
                  <TinyLine label="Contact @ Mestimate" data={ts.contactAtMestimate} />
                </>
              ) : (
                <div className="text-gray-500 text-sm">No timeseries.</div>
              )}
            </Panel>

            <Panel title="Top Listings">
              <div className="flex gap-2 mb-2">
                <TopTab label="Car Views" active={top.by==="carViews"} onClick={()=>changeTopBy("carViews")} />
                <TopTab label="Comps Opens" active={top.by==="compsOpens"} onClick={()=>changeTopBy("compsOpens")} />
                <TopTab label="Contacts" active={top.by==="contactAtMestimate"} onClick={()=>changeTopBy("contactAtMestimate")} />
              </div>

              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-2">Car</th>
                    <th className="py-2 w-20 text-right">Views</th>
                    <th className="py-2 w-20 text-right">Comps</th>
                    <th className="py-2 w-24 text-right">Contacts</th>
                  </tr>
                </thead>
                <tbody>
                  {top.rows?.length ? top.rows.map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="py-2">
                        <div className="font-medium">{r.year} {r.make} {r.model} {r.trim}</div>
                        <a className="text-blue-600 text-xs underline" href={getVehiclePath(r)} target="_blank" rel="noreferrer">View</a>
                      </td>
                      <td className="py-2 text-right">{r.views}</td>
                      <td className="py-2 text-right">{r.comps}</td>
                      <td className="py-2 text-right">{r.contacts}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="4" className="py-3 text-gray-500">No data yet.</td></tr>
                  )}
                </tbody>
              </table>
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- helpers ---------- */
function fmtYmd(d){const y=d.getFullYear();const m=String(d.getMonth()+1).padStart(2,"0");const dd=String(d.getDate()).padStart(2,"0");return `${y}-${m}-${dd}`;}
function mapStatus(arr){const m={New:0,Contacted:0,Qualified:0,Closed:0}; if(!Array.isArray(arr))return m; for(const r of arr){const k=(r?.status||"").toString();const c=Number(r?.count||0); if(k in m) m[k]=c;} return m;}
function fmtDur(s){if(s==null||isNaN(s))return null; s=Math.round(s); if(s<60)return `${s}s`; const m=Math.floor(s/60),sec=s%60; if(m<60)return `${m}m ${sec}s`; const h=Math.floor(m/60),mm=m%60; return `${h}h ${mm}m`; }

/* ---------- UI primitives ---------- */
function Card({ title, value, foot }) {
  return (
    <div className="rounded-2xl border bg-white shadow-sm p-4">
      <div className="text-sm text-gray-500">{title}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {foot && <div className="mt-2 text-xs text-gray-500">{foot}</div>}
    </div>
  );
}
function Panel({ title, children }) {
  return (
    <div className="rounded-2xl border bg-white shadow-sm p-4">
      <div className="text-sm font-medium text-gray-800">{title}</div>
      <div className="mt-3">{children}</div>
    </div>
  );
}
function TopTab({label, active, onClick}) {
  return (
    <button
      className={`px-2 py-1 text-xs rounded border ${
        active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-300"
      }`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

/* tiny SVG sparkline */
function TinyLine({ label, data }) {
  if (!Array.isArray(data) || data.length === 0)
    return <div className="text-gray-500 text-sm mb-2">{label}: —</div>;

  const w = 520, h = 60, pad = 4;
  const max = Math.max(...data, 1);
  const step = (w - pad*2) / Math.max(1, data.length - 1);
  const points = data.map((v, i) => {
    const x = pad + i*step;
    const y = pad + (h - pad*2) * (1 - v / max);
    return `${x},${y}`;
  }).join(" ");

  return (
    <div className="mb-2">
      <div className="text-xs text-gray-600 mb-1">{label}</div>
      <svg width="100%" viewBox={`0 0 ${w} ${h}`}>
        <polyline fill="none" stroke="currentColor" strokeWidth="2" points={points} />
      </svg>
    </div>
  );
}
